/**
 * gagTableColumns.js — THE column model for the Gag Breakdown table.
 *
 * ONE definition of the 15 columns: their order, their heading, the sort id
 * each maps to, and — new on 2026-08-09 — their DECLARED WIDTH.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 WHY WIDTHS EXIST NOW, AND WHY THEY MUST BE SHARED
 * ═══════════════════════════════════════════════════════════════════════════
 * The Breakdown renders **N separate `<table>`s, one per group**, all inside
 * ONE horizontal scroll region and forced to a common width by
 * `md:w-max md:min-w-full` so the group cards line up. Until 2026-08-09 those
 * tables were AUTO-LAYOUT with no `<colgroup>`, so **each group sized its own
 * columns to its own content and the widths already differed between groups**
 * — recorded in CLAUDE.md as pre-existing owed work.
 *
 * Inline row editing forced the issue rather than merely inheriting it: a
 * `<select>` or `<input>` carries its own intrinsic minimum width, so dropping
 * controls into auto-layout cells makes the divergence dramatically worse (the
 * edited group's columns jump the moment a row expands). `table-fixed` plus
 * declared widths is the fix.
 *
 * 🔑 **THE WIDTHS MUST COME FROM THIS ONE ARRAY, FOR EVERY GROUP TABLE.**
 * Per-group widths — or a second copy of these numbers anywhere — and the
 * group cards stop aligning inside the shared scroll region, which is the
 * exact symptom the `md:w-max` change was made to remove. Never hand-write a
 * width into `GagGroupTable.js`; never let one group compute its own.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHERE THE NUMBERS CAME FROM
 * ═══════════════════════════════════════════════════════════════════════════
 * Measured in headless Chromium against the COMPILED stylesheet with Inter
 * confirmed loaded, over the real dev database (`server/data/ordering.db`).
 * Harness: `docs/plans/assets/measure-breakdown-widths.js`.
 *
 * 🔑 **SIX COLUMNS ARE CONTROL-BOUND, and that is the whole reason a width
 * derived from the displayed TEXT would be wrong.** Status, Sub-dept, Shoot
 * date, D/N, INT/EXT and Owner are all wider as a `<select>`/date control than
 * as the read-only value they replace, so sizing them to the rendered text
 * would be too narrow the instant a row is expanded — and the failure would
 * look like a layout bug rather than a measurement one.
 *
 * Two are HEADER-bound (RA, INT/EXT): the heading plus its sort caret is the
 * binding constraint, not any value.
 *
 * ⚠️ **Location is CAPPED at 160 by truncation the code already applies**
 * (`max-w-[160px] truncate`). Its raw worst case is 214px; widening it to fit
 * would be an unrequested product change, so the cap stands and the value
 * keeps its `title` tooltip.
 *
 * ⚠️ **THE MEASUREMENT EXPIRES THE WAY EVERY MEASUREMENT IN THIS REPO DOES.**
 * Change a control's type, a font size, the caret box (`w-3`), or the cell
 * padding (`px-2`), and these numbers must be re-derived — not adjusted by
 * eye. Re-run the harness; do not reuse a figure from prose.
 *
 * 🔀 **DRAG-TO-RESIZE LANDED 2026-08-09 (increment 2), AND THESE WIDTHS ARE NOW
 * DEFAULTS RATHER THAN THE WIDTHS.** Product owner: *"maybe make the columns
 * manual adjustmennt by the user?"*, and — offered three scopes — he chose ONE
 * set of widths per user shared across every department.
 *
 * 🔑 **What that changes about this array, and what it does NOT.** The `width`
 * of every column is now the value a RESET returns to and the value a user with
 * no stored preference sees. It is still measured, still the number to correct
 * if a control or a label changes, and re-running the harness is still the only
 * honest way to move one. What it is no longer is a commitment: a default that
 * is 10px too narrow is now a two-second fix for the user rather than a defect
 * report, which is why the measurement notes above read less defensively than
 * `OrderTableView`'s.
 *
 * 🚨 **`min` IS NOT A SECOND OPINION ABOUT THE WIDTH — IT IS THE COLUMN'S
 * FLOOR, shared by the drag clamp and `Fit columns to screen`.** It was the
 * sortable HEADER's floor until 2026-09-27 (the order table's `order_total`
 * spilled 11.22px into its neighbour for want of one); the Breakdown's headings
 * now truncate, so it is the CONTENT's floor. See the block below the array.
 */

/**
 * The 15 columns in render order.
 *
 * - `key`     stable identity, used by the row renderer and by tests.
 * - `label`   the `<th>` text. `''` = the expand chevron (a control).
 * - `sortId`  the sort-column id, or `null` for a non-sortable control column.
 *             Ids are shared with the flat-mode Sort select — see
 *             `constants/gagBreakdown.js`.
 * - `width`   declared px width for the `<colgroup>`, and — since drag-to-resize
 *             landed — the DEFAULT a reset returns to.
 * - `min`     the smallest width a user may drag this column to, AND the floor
 *             `Fit columns to screen` squeezes it to — one floor, not two. See
 *             the block comment below the array; since 2026-09-27 it is the
 *             CONTENT's floor (headings truncate), not the heading's.
 * - `fixed`   an ICON column `Fit columns to screen` never resizes (expand, RA,
 *             Actions — owner ruling 2026-09-27). Absent = a text column.
 * - `fitFloor` (2026-09-27, owner: *"fit colums to width should use minium for
 *             Status, shoot date, RA"*) — `Fit columns to screen` pins this
 *             column at its `min`, never a proportional share; the room it
 *             frees goes to the remaining flex TEXT columns. A column-model
 *             property rather than a key list inside `fitGagColumnWidths`, so
 *             the three columns it names are visible here, beside `min`, not
 *             buried in the function that reads them. 🔑 **RA carries BOTH
 *             `fixed` (never drag-resizable — an icon column) AND `fitFloor`
 *             (Fit pins it to `min`) — its default already equals its `min`,
 *             so before this flag the two happened to agree; `fitFloor` makes
 *             that explicit rather than a coincidence `fixed`'s default-width
 *             branch produced by accident.** `expand`/`actions` carry `fixed`
 *             alone and keep TODAY'S behaviour under Fit — their DEFAULT width.
 * - `fullLabel` (2026-09-27, D/N only) — the column's full name for the sort
 *             button's `title` ("Sort by Day/Night"); `label` stays the short
 *             glyph rendered in the `<th>`. See `ui/SortableHeader`'s `sortLabel`
 *             prop, which is what reads this field.
 * - `editable` whether inline row editing offers a control in this cell.
 *
 * 🔑 The two `sortId: null` columns are the expand chevron and the Actions
 * kebab. They are CONTROLS, not data — the same rule that keeps the order
 * table's four line-item columns unsortable. All 13 DATA columns sort, which
 * is the product owner's *"I think all columns in the brerakdown can be
 * sortable"* and was already true before widths existed.
 *
 * ⚠️ `editable: false` on RA and Progress is not an omission to finish.
 * `ra_status` is DERIVED from linked risk assessments (the panel's RA section
 * is where you change it) and Progress is DERIVED from child task counts —
 * neither is a column on `gags` and neither is in the server's
 * `WRITABLE_FIELDS`. An input there would be a control with nothing to write.
 *
 * 🔀 **`scenes` WENT `editable: false` → `true` ON 2026-08-09, AND ITS OLD
 * REASON IS WORTH KNOWING BECAUSE IT WAS A GOOD ONE.** The prototype withheld
 * it because `PUT /api/gags/:gagId` takes `scenes` through its OWN parse/replace
 * path (`parseScenes` → `replaceScenes`), separate from `WRITABLE_FIELDS`, and
 * *"wiring a bare text input to it without exercising that parser is how a
 * comma-handling bug ships silently"*. That reasoning is intact — what changed
 * is that the cell is no longer a bare text input. It is a MULTI-SELECT over
 * scene numbers harvested from the department (`GET /api/gags/scene-suggestions`),
 * and the parser is exercised on both sides: `utils/sceneOptions.js` mirrors
 * `parseScenes` and both suites run one shared case table. Product owner's
 * ruling; see `docs/superpowers/specs/2026-08-09-breakdown-inline-row-editing-design.md` §3.
 *
 * 📏 **Its 120px is unchanged and did NOT need re-measuring**, which is the
 * exception to the six control-bound columns above. Those are bound by a
 * `<select>`/date control's intrinsic minimum; the Scenes control is a
 * `ui/Dropdown` whose TRIGGER truncates (`w-full`, no intrinsic floor) and
 * whose PANEL is absolutely positioned and so escapes the cell entirely.
 */
import { fitColumnWidths } from '../utils/fitColumnWidths';
import { tightenPhoneColumns } from './phoneLayout';

export const GAG_TABLE_COLUMNS = [
  { key: 'expand', label: '', sortId: null, width: 40, min: 40, fixed: true, editable: false },
  { key: 'title', label: 'Title', sortId: 'title', width: 150, min: 60, editable: true },
  { key: 'status', label: 'Status', sortId: 'status', width: 150, min: 80, editable: true, fitFloor: true },
  { key: 'ra', label: 'RA', sortId: 'ra', width: 50, min: 50, fixed: true, editable: false, fitFloor: true },
  { key: 'scenes', label: 'Scenes', sortId: 'scene', width: 120, min: 60, editable: true },
  { key: 'shoot', label: 'Shoot date', sortId: 'shoot', width: 160, min: 100, editable: true, fitFloor: true },
  { key: 'unit', label: 'Unit', sortId: 'unit', width: 130, min: 90, editable: true },
  { key: 'day_night', label: 'D/N', fullLabel: 'Day/Night', sortId: 'day_night', width: 90, min: 60, editable: true },
  { key: 'int_ext', label: 'INT/EXT', sortId: 'int_ext', width: 90, min: 50, editable: true },
  { key: 'location', label: 'Location', sortId: 'location', width: 160, min: 60, editable: true },
  // 🔀 Sub-dept sits immediately BEFORE Owner (owner 2026-10-19: "changing one
  // effects the options in the other"). Widths are keyed by column KEY
  // (`sfx_breakdown_col_widths`), so the move keeps every stored width.
  { key: 'sub_department', label: 'Sub-dept', sortId: 'sub_department', width: 140, min: 60, editable: true },
  { key: 'owner', label: 'Owner', sortId: 'owner', width: 180, min: 60, editable: true },
  { key: 'priority', label: 'Priority', sortId: 'priority', width: 100, min: 80, editable: true },
  { key: 'progress', label: 'Progress', sortId: 'progress', width: 110, min: 72, editable: false },
  { key: 'actions', label: 'Actions', sortId: null, width: 60, min: 60, fixed: true, editable: false },
];

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 WHERE EVERY `min` ABOVE COMES FROM — ONE FLOOR PER COLUMN (2026-09-27)
 * ═══════════════════════════════════════════════════════════════════════════
 * Owner, 2026-09-27: *"reset colum widths should be changed to fit column
 * widths so that we try our best to fit on desktop view. Truncate where needed
 * to try and make it fit"* — he chose "Squeeze text columns": icon columns
 * fixed, text columns shrink in proportion to their defaults down to roughly
 * 60px, cut off with an ellipsis, full text on hover, headings truncating the
 * same way.
 *
 * 🔀 **THE FLOOR IS NOW THE CONTENT, NOT THE HEADING — this block said the
 * opposite until 2026-09-27 and that premise is RETIRED, not violated.** The
 * old floors were each heading's measured `label + gap-1 + w-3 caret + px-2`
 * rounded up to ten (Title 68.22→70 … Progress 101.30→110; the table is at
 * `git show 6589049f:client/src/constants/gagTableColumns.js`) because a
 * heading could not truncate. The Breakdown's headings now DO truncate
 * (`ui/SortableHeader`'s opt-in `truncateLabel`), their full text stays on the
 * sort button's `title`, and the old floors summed to 1,240px — more than the
 * table region at 1280 (964px measured), so no fit could ever have reached a
 * laptop without going below them.
 *
 * 🚨 **THE DRAG CLAMP AND THE FIT FLOOR ARE THE SAME NUMBER BY NECESSITY, not
 * taste.** `hooks/useColumnWidths`' `sanitise` DISCARDS a stored width below
 * `min` on the next load, so a fit saved below `min` would silently revert on
 * reload. One floor per column is what makes Fit persist.
 *
 * The floors, each measured in headless Chromium against the compiled
 * stylesheet with Inter loaded (widest realistic cell content, padding
 * included):
 *
 *   TEXT (ellipsis + a full-value `title`): Title · Sub-dept · Scenes ·
 *     Location · Owner → **60**, the owner's number.
 *   Unit → **90**, default **130** (owner ruling 2026-10-19): the editing
 *     row's Unit box is `ui/SuggestInput`, whose ▾ takes ~28px, so at 60 it
 *     showed one letter. 🪤 `sanitise` discards a stored Unit width below 90,
 *     so such a user falls back to 130 on next load.
 *   Status → **80**: To Do 65.30 / Done 62.42 / Blocked 78.50 whole; In
 *     Progress 97.53, Cancelled 90.03, Ready to Shoot 119.91 ellipse INSIDE the
 *     pill (colour intact) with the label on the cell's `title`.
 *   Shoot date → **100**: dot + every 3-letter-month date (widest 99.63) whole;
 *     only `Sept` (en-GB, up to 120.25) ellipses, full date on the cell title.
 *   D/N → **60**: Dawn 53.70 is the widest label — never truncates.
 *   INT/EXT → **50**: EXT 42.45 — never truncates (only the heading does).
 *   Priority → **80**: Medium 78.95 is the widest pill — never truncates.
 *   Progress → **72**: the bar's `min-w-[56px]` + `px-2`; the `d/t` count is
 *     the part that clips (it is on the cell's `title`), never the bar.
 *   expand 40 · RA 50 · Actions 60 → `fixed`, their own shipped width, and Fit
 *     never moves them. They have no text to truncate.
 *
 * The TEXT floors sum to 832px for the twelve text columns (802 until Unit's
 * floor rose to 90 on 2026-10-19), against the 814px those columns get at 1280
 * with the sidebar open (964px pane − 150 fixed) — so since 2026-10-19 Fit at
 * that width puts every column at its floor and the table overflows by 18px
 * (drag-to-pan) — a consequence of the owner's Unit ruling, flagged to him.
 * ⚠️ **A floor EXPIRES WITH ITS CONTENT** — a new status label, a longer
 * priority, a date format change: re-measure, never reason a value from an old
 * one.
 */

/**
 * The widest a user may drag ANY column. One ceiling, not thirteen.
 *
 * 🔑 **Why a single number rather than a per-column maximum.** A minimum is a
 * correctness bound — it is the width below which a specific heading overflows,
 * so it is necessarily per column. A maximum is not: no width is *wrong*, it is
 * only unreasonable, and "unreasonable" does not vary by column. Thirteen
 * hand-picked ceilings would be thirteen numbers with no measurement behind
 * them, which is the shape this repo keeps getting bitten by.
 *
 * **Why 600.** The widest default here is Owner at 180, so 600 is more than
 * three times the widest column anyone has argued for — comfortably past any
 * real use (the widest untruncated value the harness found anywhere was
 * Location's 214.42px) and short of the range where a single column stops
 * being a column. It caps the whole 15-column table at 9,000px rather than the
 * 60,000px a stray `Number.MAX_SAFE_INTEGER` from a hand-edited localStorage
 * value would otherwise produce inside a shared horizontal scroll region.
 *
 * 🔑 **The clamp is also what makes the reset affordances non-urgent rather
 * than a rescue.** A user physically cannot drag a column to 4px or to 4000px,
 * so double-click-to-reset and `Actions ▾ → Fit columns to screen` (which
 * replaced `Reset column widths` 2026-09-27) are conveniences, not the only way back out of a mis-drag.
 */
export const MAX_GAG_COLUMN_WIDTH = 600;

/**
 * `localStorage` key for the user's column widths.
 *
 * 🔑 **ONE SET OF WIDTHS PER USER, SHARED ACROSS EVERY DEPARTMENT — the product
 * owner's explicit choice from three offered scopes.** So the key carries no
 * department id and no user id: it is a display preference of exactly the same
 * kind as `sfx_dark_mode` and `sfx_page_size`, and it
 * follows the house `sfx_` prefix. **Do not "improve" this into
 * `sfx_breakdown_col_widths_${departmentId}`** — per-department widths were
 * offered and declined, and the migration cost of splitting a shared key later
 * is a silent revert to defaults for everyone who had set one.
 *
 * 🪤 **`npm run check-bloat` CANNOT SEE THIS KEY, IN EITHER DIRECTION.** Its
 * orphan-key scan is `/localStorage\.setItem\s*\(\s*['"]([^'"]+)['"]/`
 * (`scripts/check-bloat.js:230`) — a **string literal only** — and this key is
 * passed as a constant, so it will never be reported as an orphan and never be
 * reported at all. **Grep `sfx_breakdown_col_widths` by hand** before
 * concluding it is unused. That is the convention rather than an exception: 41
 * `localStorage.setItem(CONST, …)` sites under `client/src` already share the
 * blind spot, and `sfx_breakdown_view` was deleted in 2026-08-07 precisely
 * because a constant-keyed key had gone invisible to the scan.
 */
export const GAG_COL_WIDTHS_KEY = 'sfx_breakdown_col_widths';

/**
 * The defaults, as the same `{ [columnKey]: px }` map shape the hook and
 * `localStorage` use.
 *
 * 🚨 **A MAP KEYED BY COLUMN KEY, NEVER AN ARRAY BY INDEX.** An index array
 * silently mis-assigns every width after any column is added, removed,
 * reordered or hidden — and this table has ALREADY had one hidden (the
 * `Effect` / `primary_effect_type` column, see `GagGroupTable.js`). Keying by
 * `key` is what makes a stored value self-healing and is why the format needs
 * no version field.
 */
export const DEFAULT_GAG_COLUMN_WIDTHS = Object.fromEntries(
  GAG_TABLE_COLUMNS.map((c) => [c.key, c.width])
);

/**
 * `Fit columns to screen` (owner ruling 2026-09-27) — the width map that makes
 * the table exactly `available` px wide, or `null` when there is nothing
 * measured to fit to (the caller then offers nothing — never a guessed width).
 *
 * - `fixed` columns keep their DEFAULT width, whatever the user had dragged
 *   them to — so the result is a function of `available` alone, which is what
 *   lets the menu item say "already fits" by comparing maps.
 * - every other column scales in proportion to its DEFAULT width, never below
 *   its `min` (the one floor — see the block above) and never above `maxWidth`.
 *   A column pinned at a bound drops out and the rest are re-scaled over what
 *   is left (water-filling), so a pinned column's shortfall is spread over the
 *   others rather than lost.
 * - if the floors alone exceed the room, every text column sits at its floor:
 *   the table overflows and drag-to-pan covers the rest (owner ruling).
 * - integer px; the rounding remainder goes to the largest fractional parts
 *   (column order breaks ties), so the sum is EXACTLY `available` whenever the
 *   bounds allow it. Only when every text column is at `maxWidth` is it less —
 *   no real screen; the table's `w-full` stretch covers it.
 *
 * Pure: no DOM, no storage. The caller saves the result through the hook's
 * own setter, the one write path.
 *
 * 🔀 **`fitFloor` COLUMNS (Status, Shoot date, RA — 2026-09-27) ARE PINNED AT
 * THEIR `min`, NEVER SCALED.** Owner: *"fit colums to width should use minium
 * for Status, shoot date, RA"*. They are folded into the same fixed-sum pass
 * as `fixed` columns — `fitFloor` wins over `fixed` when a column (RA) carries
 * both, so RA's pin is driven by `min` explicitly rather than by its `fixed`
 * default width happening to equal it. The room they free is spread over the
 * remaining flex TEXT columns exactly as before.
 *
 * 🔀 **THE ALGORITHM ITSELF MOVED to `utils/fitColumnWidths.js` on
 * 2026-09-28** (owner: *"reset column widths should use the same code and
 * changes as Fit columns to screen on the Breakdown page. same for other
 * tables"*) — it was already domain-free (parameterised by `columns` and
 * `maxWidth`), so the Breakdown was simply its first caller. This is now a
 * THIN WRAPPER carrying the Breakdown's own defaults; every rule documented
 * above still governs, and the arithmetic itself is documented once, at the
 * shared function. **Never re-inline the algorithm here or anywhere else —
 * import `fitColumnWidths` and supply your own column model + ceiling.**
 */
export function fitGagColumnWidths(available, columns = GAG_TABLE_COLUMNS, maxWidth = MAX_GAG_COLUMN_WIDTH) {
  return fitColumnWidths(available, columns, maxWidth);
}

/**
 * The table's floor width **at the DEFAULT widths** — a `reduce`, never a
 * literal.
 *
 * 🔀 **SINCE DRAG-TO-RESIZE THIS IS NO LONGER THE NUMBER THE TABLE RENDERS AT,
 * AND MISTAKING IT FOR ONE IS THE MOST LIKELY WAY TO BREAK GROUP ALIGNMENT.**
 * The live floor is the sum of the user's CURRENT widths — `totalWidth` from
 * `hooks/useColumnWidths.js` — and it is what both the group-card list wrapper
 * and every group's `<table>` must be given. This constant survives as the
 * value that sum returns to when every column is double-click-reset, and as the first-paint
 * fallback for a surface that renders the table without the hook.
 *
 * 🔢 **DO NOT QUOTE THIS NUMBER ANYWHERE.** `OrderTableView`'s equivalent
 * moved five times in a single day and every derived figure quoted in prose
 * went stale with it. Import the constant.
 *
 * It pairs with `w-full` on the table exactly as the order table's does:
 * `w-full` fills the scrollport, the `minWidth` keeps the floor so a narrow
 * screen scrolls horizontally inside the region rather than crushing 13 data
 * columns. Spare width is spread across every column in proportion to its
 * declared width (fixed-table stretch-to-fit).
 */
export const GAG_TABLE_MIN_WIDTH = GAG_TABLE_COLUMNS.reduce((sum, c) => sum + c.width, 0);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FROZEN COLUMNS (2026-08-09) — the expand chevron AND Title, as ONE group
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Product owner, after widening a column: *"Title column can get pushed off the
 * left hand of the screen if I make it or another column too big"*, then, on
 * freezing it: *"will freezint the title column help?"*
 *
 * 🔑 **BOTH, TOGETHER, AND THAT IS NOT A GENEROUS READING OF "the title
 * column".** The chevron is the control that opens the row Title identifies;
 * freezing Title alone leaves the chevron scrolling away from the row it opens,
 * so the row you can still read is the one you can no longer expand. They are
 * the row's identity and they are treated as one frozen group.
 *
 * 🔴 **NOTHING ELSE IS FROZEN, and adding a third column is a PRODUCT decision,
 * not a tidy-up.** Every frozen column is horizontal space permanently spent:
 * it is subtracted from the scrollport for every other column at every scroll
 * offset, on a table the owner already thinks is too wide. At the declared
 * defaults these two are 190px of a ~1,720px table; a user may drag either of
 * them to 600, so their DECLARED ceiling is 1,200px and the floor is
 * the sum of their two `min`s (100 since 2026-09-27). ⚠️ **1,200px is no longer what RENDERS — see
 * `FROZEN_BLOCK_MAX_FRACTION` below.** A block that reached the scrollport's
 * width made every other column unreachable at every scroll offset, so the
 * block is bounded at half the measured region; a third frozen column would
 * come out of that same bounded budget, which is one more reason it needs the
 * owner. A sticky-RIGHT Actions column was weighed and
 * deferred separately (see `GagGroupTable.js`); it is a different question and
 * it costs the same kind of width.
 *
 * ⚠️ **ORDER MATTERS: this array must be a PREFIX of `GAG_TABLE_COLUMNS`, in
 * the same order.** The offsets below are a running sum, so a frozen column
 * that is not physically leftmost would be pinned to a place it does not
 * occupy and would sit on top of a column it is not.
 */
export const FROZEN_GAG_COLUMN_KEYS = ['expand', 'title'];

/**
 * The `left` offset each frozen column pins at, derived from the LIVE width map.
 *
 * 🚨 **IT MUST BE THE LIVE WIDTHS, NEVER `DEFAULT_GAG_COLUMN_WIDTHS`.** Frozen
 * columns stay resizable, so Title's offset IS the chevron column's current
 * width — read the defaults instead and dragging the chevron leaves either a
 * transparent gap between the two frozen columns (widened) or Title sitting on
 * top of the chevron (narrowed). Neither is visible in jsdom and both are
 * visible immediately in a browser.
 *
 * The same fallback rule the `<colgroup>` uses: a key missing from a stored
 * layout (a column added since it was written) falls back to that column's
 * measured default rather than summing `undefined` into `NaN`, which would put
 * `left: NaN` on every frozen cell at once and silently unfreeze the table.
 *
 * 🪤 **THESE ARE DECLARED PIXELS, AND THE TABLE SOMETIMES RENDERS STRETCHED
 * ONES — which is harmless HERE and is not in the resize handle.** The table is
 * `w-full` with `minWidth: totalWidth`, so when the declared total is NARROWER
 * than the scrollport every column is scaled up proportionally and the chevron
 * renders wider than `widths.expand`. `ui/ColumnResizeHandle` has to undo that
 * factor because a drag happens in rendered pixels. A sticky offset does not:
 * in exactly the case where the table is stretched it also FITS, so there is no
 * horizontal scroll, and a `left` that is never smaller than the element's
 * natural position never binds. Verified in a browser at both widths rather
 * than argued — the residual case (a scrollport a few px narrower than the
 * content, where the group card's 1px borders leave ~5px of scroll range) pins
 * Title a fraction of a pixel inside the chevron's right edge.
 */
export function frozenGagColumnOffsets(widths) {
  const byKey = new Map(GAG_TABLE_COLUMNS.map((c) => [c.key, c]));
  const offsets = {};
  let acc = 0;
  for (const key of FROZEN_GAG_COLUMN_KEYS) {
    const col = byKey.get(key);
    if (!col) continue;
    offsets[key] = acc;
    const w = widths && Number.isFinite(widths[key]) ? widths[key] : col.width;
    acc += w;
  }
  return offsets;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THE FROZEN BLOCK MAY NEVER FILL THE SCROLLPORT — THE BOUND, AND WHY IT IS
 * A FRACTION OF A MEASUREMENT RATHER THAN A NUMBER (2026-08-09)
 * ═══════════════════════════════════════════════════════════════════════════
 * Found by the hardening browser sweep. `expand` and `title` are both frozen AND
 * both draggable to the shared `MAX_GAG_COLUMN_WIDTH`, so the frozen block's
 * ceiling was **1,200px** against a scroll region that measures **976px at
 * 1280** and ~720px at 1024. A frozen column is painted over the region's left
 * edge at every scroll offset, so once the block is as wide as the region there
 * is no strip left for anything else: **the entire rest of the table becomes
 * unreachable, permanently**, because the widths are persisted.
 *
 * 📏 **MEASURED, not reasoned — own Chromium, own port, own copy of the dev
 * database (dept 213, five groups), 41 scroll positions swept per case with
 * `elementFromPoint`:**
 *
 *     frozen block   region   non-frozen columns reachable
 *        190 (default)  976    13 / 13
 *        900            976    13 / 13
 *        975            976     1 / 13   ← a 1px strip
 *      1,000            976     0 / 13   (1,111 in-viewport probes, all frozen)
 *      1,200            976     0 / 13   (986 in-viewport probes, all frozen)
 *
 * 🔑 **THE CORRECTNESS THRESHOLD IS EXACTLY `block < region`, and it is
 * provable rather than merely observed.** Content at offset `x` is visible iff
 * some `scrollLeft` `s` satisfies `s + block <= x < s + region`; that interval
 * is non-empty iff `block < region`. The table above is that algebra measured:
 * 975 against 976 leaves a one-pixel strip and exactly one column partly
 * reachable, and 1,000 leaves none.
 *
 * 🔴 **SO WHY HALF, AND NOT `block < region`.** `block < region` is the bound
 * that stops the table being *lost*; it is not a bound that leaves it *usable* —
 * the 975px row above is the counter-example, and a user who has to hunt a 1px
 * strip has the defect in every way that matters. Half the scrollport is the
 * smallest simple fraction that leaves a working table: at 1280 it caps the
 * block at 488px and leaves a 488px strip, wider than any column's default bar
 * none; at 1024 it caps at 360px, still wider than the widest default (Owner,
 * 180). It also keeps the frozen block a minority of the surface, which is the
 * product argument this file already makes for freezing only two columns —
 * *"every frozen column is horizontal space permanently spent"*.
 *
 * 🚨 **IT IS A FRACTION OF A LIVE MEASUREMENT, NOT A CONSTANT, AND THAT IS THE
 * WHOLE POINT.** A fixed cap that is right at 1280 is wrong at 1024, and — the
 * case a fixed cap misses entirely — **a stored width outlives the viewport that
 * produced it.** Drag Title wide on a 2560px monitor, undock the laptop, and a
 * drag-time-only clamp has already been passed; a stored-value clamp has already
 * been passed too. Only a bound re-derived from the CURRENT region width holds
 * across a window resize, a sidebar collapse, a different machine and a reload.
 *
 * 🔑 **IT BOUNDS WHAT IS RENDERED, NEVER WHAT IS STORED — and that asymmetry is
 * deliberate.** Writing the bounded value back would let one narrow window
 * permanently destroy a preference chosen on a wide one, which is a worse defect
 * than the one being fixed and is silent. The user's chosen width survives in
 * `sfx_breakdown_col_widths`; it is honoured wherever there is room for it and
 * bounded where there is not. The visible consequence, which is correct and
 * should not be "fixed": dragging a frozen column past the bound stops moving
 * the column while still recording the intent, so the width appears on a wider
 * screen and never on a screen that cannot afford it.
 *
 * ⚠️ **THE FLOORS WIN OVER THE CAP.** No frozen column is taken below its own
 * `min` (expand 40 + title 60 = 100 since 2026-09-27), because the `min` block
 * above is the column's floor and trading one defect for another is not a
 * fix. So on a region narrower than 200px the block can
 * still exceed half of it — that is below every supported viewport and below
 * `md`, where the table is not rendered at all.
 *
 * ⚠️ **Mildly conservative on very wide screens, knowingly.** The bound is
 * applied to DECLARED widths. When the declared total is narrower than the
 * scrollport the table stretches and nothing can be off screen, yet a block over
 * half the region is still shrunk. Reachable only above ~2260px with the block
 * already past half; the cost is that the other columns get the difference,
 * which is what stretch-to-fit does anyway. A rule that switched itself off in
 * the stretched case would be a second code path guarding a case that cannot
 * produce the defect.
 *
 * 🪤 **jsdom composites nothing, lays nothing out and scrolls nothing, so
 * REACHABILITY IS A BROWSER CHECK.** What is unit-testable is this arithmetic
 * and the fact that the component feeds it the measurement — both are pinned
 * (`gagTableColumns.frozenBound.test.js`,
 * `GagGroupTable.frozenBound.test.js`), and both files say so in their headers.
 */
export const FROZEN_BLOCK_MAX_FRACTION = 0.5;

/**
 * The widest the frozen block may render at, for a measured region width.
 * `null` means "no measurement yet" — the caller must then change nothing,
 * because guessing a region width is how the `100vw` trap in
 * `hooks/useMeasuredWidth.js` gets re-invented.
 */
export function frozenBlockMaxWidth(regionWidth, fraction = FROZEN_BLOCK_MAX_FRACTION) {
  if (!Number.isFinite(regionWidth) || regionWidth <= 0) return null;
  return Math.floor(regionWidth * fraction);
}

/**
 * The width map to RENDER, given the stored map and the live region width.
 *
 * 🔑 **Returns the input object UNCHANGED (same reference) whenever the block
 * already fits**, so the common case adds no work and breaks no `useMemo`
 * identity downstream — `frozenGagColumnOffsets`, the `<colgroup>` and
 * `totalWidth` all key off this object.
 *
 * 🪤 **Every stored value is admitted by `Number.isFinite`, never by a
 * comparison** — `'600' <= 600` is `true`, so a comparison-only guard accepts
 * the STRING and the surplus arithmetic below silently concatenates. Same rule
 * as `useColumnWidths`'s `sanitise` and `frozenGagColumnOffsets`, and the test
 * file carries a string case for it.
 *
 * The surplus is taken in proportion to each frozen column's HEADROOM above its
 * own floor, so a column already at its floor contributes nothing — which in
 * practice means `expand` (40, at its floor) is left alone and `title` absorbs
 * the whole reduction, without that being special-cased for two columns.
 */
export function boundFrozenColumnWidths(widths, regionWidth, fraction = FROZEN_BLOCK_MAX_FRACTION) {
  const cap = frozenBlockMaxWidth(regionWidth, fraction);
  if (cap == null) return widths;

  const byKey = new Map(GAG_TABLE_COLUMNS.map((c) => [c.key, c]));
  const cols = FROZEN_GAG_COLUMN_KEYS.map((k) => byKey.get(k)).filter(Boolean);
  const floorOf = (c) => (Number.isFinite(c.min) ? c.min : 0);

  const eff = {};
  let sum = 0;
  for (const c of cols) {
    const raw = widths && Number.isFinite(widths[c.key]) ? widths[c.key] : c.width;
    eff[c.key] = Math.max(floorOf(c), Math.round(raw));
    sum += eff[c.key];
  }
  if (sum <= cap) return widths;

  // One pass per column is enough: a column driven to its floor drops out of
  // `totalRoom`, so the next pass redistributes what it could not absorb.
  for (let pass = 0; pass < cols.length && sum > cap; pass += 1) {
    const room = cols.map((c) => eff[c.key] - floorOf(c));
    const totalRoom = room.reduce((a, b) => a + b, 0);
    if (totalRoom <= 0) break;
    const take = Math.min(sum - cap, totalRoom);
    let allocated = 0;
    cols.forEach((c, i) => {
      if (room[i] <= 0) return;
      const share = Math.min(room[i], Math.round((room[i] / totalRoom) * take));
      eff[c.key] -= share;
      allocated += share;
    });
    // Rounding can leave a pixel or two unallocated (never more than one per
    // column). Take the remainder from whichever column still has the most
    // headroom, so the result is deterministic rather than order-dependent.
    let short = take - allocated;
    while (short > 0) {
      let best = -1;
      let bestRoom = 0;
      cols.forEach((c, i) => {
        const r = eff[c.key] - floorOf(c);
        if (r > bestRoom) { bestRoom = r; best = i; }
      });
      if (best < 0) break;
      const give = Math.min(short, bestRoom);
      eff[cols[best].key] -= give;
      short -= give;
    }
    sum = cols.reduce((s, c) => s + eff[c.key], 0);
  }

  return { ...(widths || {}), ...eff };
}

/** Column keys that inline row editing offers a control for. */
export const GAG_EDITABLE_COLUMN_KEYS = GAG_TABLE_COLUMNS
  .filter((c) => c.editable)
  .map((c) => c.key);

/**
 * Column key → the `gags` field an inline edit writes.
 *
 * 🔑 **The key is NOT always the field, and the two that differ are the two
 * that would fail silently.** `sub_department` writes `sub_department_id` and
 * `shoot` writes `shoot_date`; a `PUT` body carrying the column key instead
 * would be ignored by the server's `WRITABLE_FIELDS` loop
 * (`routes/gags.js:1347` — `if (!(field in req.body)) continue`), returning
 * 200 with nothing changed. Same shape as the order table's `sortId` trap.
 *
 * `owner` maps to `owner_id`, which the same `PUT` accepts but through a
 * SEPARATE permission ladder — see `GagRowEditCell.js`.
 *
 * 🪤 **`scenes` is the third odd one, and it is odd in a different way: it is
 * not in `WRITABLE_FIELDS` at all.** The handler tests for it separately
 * (`hasFieldEdits = WRITABLE_FIELDS.some(f => f in req.body) || 'scenes' in
 * req.body`, `routes/gags.js:1306`) and then runs it through `parseScenes` →
 * `replaceScenes` rather than the `UPDATE gags SET` loop. So it IS gated by
 * `canEditGag` exactly like a field edit — but its VALUE is a comma-separated
 * string, not a column value, and it writes to `gag_scenes`. Listing it here is
 * what makes the row's diff builder send it; do not "tidy" it out on the
 * grounds that it is not a `gags` column.
 */
export const GAG_COLUMN_FIELD = {
  title: 'title',
  status: 'status',
  scenes: 'scenes',
  sub_department: 'sub_department_id',
  shoot: 'shoot_date',
  unit: 'unit',
  day_night: 'day_night',
  int_ext: 'int_ext',
  location: 'location',
  owner: 'owner_id',
  priority: 'priority',
};

/**
 * ── THE PHONE TABLE (below `md`, 2026-10-03 PROTOTYPE) ──────────────────
 * Owner: the Breakdown's phone list becomes a TABLE in the house pattern the
 * three order pages use on phones (`OrderTableView.js`'s phone sets), opening
 * FLAT in shoot-date order like desktop. It replaced the `md:hidden` card list.
 * Rendered through the shared `ui/PageScrollTable` (pinned header pane, x-only
 * rows, every column a `min`) — never a second hand-rolled shell.
 * 🔀 OWNER CHANGE (same day): WIDER, with sideways scroll like the order
 * phone tables (the PAGE never overflows — the table pane scrolls); all
 * display-only, editing stays in the expanded panel; first column NOT frozen.
 * Gag · Shoot · Status are on screen at 375. Originally:
 * Columns: Gag (title + muted "scenes · sub-department") · Shoot (compact date
 * + the deadline dot) · Status (pill) · the row kebab (no heading; the deleted
 * view's Restore lives only there) — Owner was dropped in that first cut and
 * is BACK as a column now. Cells are emitted in THIS array's order, by key
 * (the `OrderMobileTableRow` rule). `sortField` is the page's own sort id, so
 * a header tap runs the SAME `onHeaderSort` desktop does.
 * ⚠️ Widths are prototype estimates, browser-checked at 360/375 only.
 */
export const GAG_PHONE_TABLE_COLUMNS = Object.freeze(tightenPhoneColumns([
  Object.freeze({ key: 'title', label: 'Gag', sortField: 'title', width: 140, min: 90 }),
  Object.freeze({ key: 'shoot', label: 'Shoot', sortField: 'shoot', width: 96, min: 84 }),
  Object.freeze({ key: 'status', label: 'Status', sortField: 'status', width: 100, min: 80 }),
  Object.freeze({ key: 'owner', label: 'Owner', sortField: 'owner', width: 80, min: 64 }),
  Object.freeze({ key: 'sub_department', label: 'Sub-dept', sortField: 'sub_department', width: 100, min: 70 }),
  Object.freeze({ key: 'scenes', label: 'Scenes', sortField: 'scene', width: 80, min: 64 }),
  Object.freeze({ key: 'day_night', label: 'D/N', sortField: 'day_night', width: 56, min: 52 }),
  Object.freeze({ key: 'int_ext', label: 'INT/EXT', sortField: 'int_ext', width: 72, min: 68 }),
  Object.freeze({ key: 'location', label: 'Location', sortField: 'location', width: 110, min: 70 }),
  Object.freeze({ key: 'unit', label: 'Unit', sortField: 'unit', width: 72, min: 56 }),
  Object.freeze({ key: 'priority', label: 'Priority', sortField: 'priority', width: 84, min: 76 }),
  Object.freeze({ key: 'ra', label: 'RA', sortField: 'ra', width: 48, min: 44 }),
  Object.freeze({ key: 'progress', label: 'Progress', sortField: 'progress', width: 96, min: 80 }),
  Object.freeze({ key: 'actions', label: '', ariaLabel: 'Actions', width: 40, min: 40 }),
]));
/** Its OWN width key — a different column set must never share a key. 🪤 A
 *  constant: grep `sfx_breakdown_mobile_col_widths` by hand. */
export const GAG_PHONE_COL_WIDTHS_KEY = 'sfx_breakdown_mobile_col_widths';
